import { useEffect, useMemo, useState } from 'react';
import BottomSheet from './BottomSheet';
import NumPad from './NumPad';
import { useBranches } from '../../../hooks/useBranches';
import { resolveBranchRef } from '../../../utils/branchResolver';

const QuickRegisterSheet = ({
  open,
  onClose,
  courses = [],
  onSubmit,
  saving,
  error,
}) => {
  const {
    activeList: activeBranches,
    byId: branchesById,
    bySlug: branchesBySlug,
  } = useBranches();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [courseId, setCourseId] = useState('');
  const [batchId, setBatchId] = useState('');
  const [branchChoice, setBranchChoice] = useState('__auto__');
  const [showPhonePad, setShowPhonePad] = useState(false);
  const [useTouchNumberPad, setUseTouchNumberPad] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('0');
    setCourseId('');
    setBatchId('');
    setBranchChoice('__auto__');
    setShowPhonePad(false);
  }, [open]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia(
      '(pointer: coarse), (max-width: 1024px)',
    );
    const syncNumberPadMode = () => setUseTouchNumberPad(mediaQuery.matches);
    syncNumberPadMode();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncNumberPadMode);
      return () => mediaQuery.removeEventListener('change', syncNumberPadMode);
    }

    mediaQuery.addListener(syncNumberPadMode);
    return () => mediaQuery.removeListener(syncNumberPadMode);
  }, []);

  const selectedCourse = useMemo(
    () => courses.find((course) => course.id === courseId) || null,
    [courses, courseId],
  );

  const batches = useMemo(() => selectedCourse?.batches || [], [selectedCourse]);
  const selectedBatch = useMemo(
    () =>
      batches.find(
        (batch, index) =>
          String(batch.batchId || index) === String(batchId),
      ) || null,
    [batches, batchId],
  );
  const branchCatalogs = useMemo(
    () => ({ byId: branchesById, bySlug: branchesBySlug }),
    [branchesById, branchesBySlug],
  );
  const autoBranch = useMemo(
    () => resolveBranchRef(selectedBatch || selectedCourse?.branch, branchCatalogs),
    [selectedBatch, selectedCourse, branchCatalogs],
  );
  const selectedBranch = useMemo(() => {
    if (branchChoice === '__auto__') return autoBranch || null;
    if (branchChoice === '__none__') return null;
    return activeBranches.find((branch) => branch.id === branchChoice) || null;
  }, [activeBranches, autoBranch, branchChoice]);

  useEffect(() => {
    if (!batches.length) {
      setBatchId('');
      return;
    }
    setBatchId(batches[0].batchId || '0');
  }, [batches]);

  const canSubmit =
    !saving &&
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    phone.trim() &&
    courseId;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit?.({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim().toLowerCase(),
      phone: phone.trim(),
      courseId,
      batchId,
      branch: selectedBranch,
    });
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Quick register student"
      description="Minimal info now. Student can complete their profile after first login."
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[52px] rounded-2xl border border-stone-300 bg-white px-5 text-sm font-semibold text-stone-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="min-h-[52px] rounded-2xl bg-[#d61f74] px-5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create account'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-stone-700">
              First name
            </span>
            <input
              type="text"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              autoComplete="given-name"
              className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-stone-700">
              Last name
            </span>
            <input
              type="text"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              autoComplete="family-name"
              className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </label>
        </div>

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-stone-700">
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            placeholder="student@example.com"
            className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </label>

        <div>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-stone-700">
              Phone
            </span>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(event) =>
                setPhone(event.target.value.replace(/[^0-9]/g, '').slice(0, 15))
              }
              onFocus={() => {
                if (!phone) setPhone('0');
                if (useTouchNumberPad) setShowPhonePad(true);
              }}
              placeholder="07XXXXXXXX"
              className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 font-mono text-base text-stone-950 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </label>
          {useTouchNumberPad && showPhonePad ? (
            <div className="mt-3 rounded-2xl border border-stone-200 bg-stone-50 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Number pad
                </span>
                <button
                  type="button"
                  onClick={() => setShowPhonePad(false)}
                  className="text-xs font-semibold text-stone-600 hover:text-stone-900"
                >
                  Hide
                </button>
              </div>
              <NumPad
                value={phone}
                onChange={(nextValue) =>
                  setPhone(String(nextValue).replace(/[^0-9]/g, '').slice(0, 15))
                }
                maxLength={15}
              />
            </div>
          ) : null}
        </div>

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-stone-700">
            Course
          </span>
          <select
            value={courseId}
            onChange={(event) => setCourseId(event.target.value)}
            className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="">Select a course</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.name || course.title || course.id}
              </option>
            ))}
          </select>
        </label>

        {batches.length > 1 ? (
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-stone-700">
              Batch
            </span>
            <select
              value={batchId}
              onChange={(event) => setBatchId(event.target.value)}
              className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            >
              {batches.map((batch, index) => {
                const branchName =
                  resolveBranchRef(batch, {
                    byId: branchesById,
                    bySlug: branchesBySlug,
                  })?.name || '';
                return (
                  <option
                    key={batch.batchId || index}
                    value={batch.batchId || String(index)}
                  >
                    {batch.batchId || `Batch ${index + 1}`}
                    {branchName ? ` - ${branchName}` : ''}
                  </option>
                );
              })}
            </select>
          </label>
        ) : null}

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-stone-700">
            Branch
          </span>
          <select
            value={branchChoice}
            onChange={(event) => setBranchChoice(event.target.value)}
            className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="__auto__">
              {autoBranch?.name ? `Auto: ${autoBranch.name}` : 'Auto from batch/course'}
            </option>
            {activeBranches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
            <option value="__none__">No branch</option>
          </select>
        </label>

        {error ? (
          <p className="rounded-2xl bg-red-50 px-4 py-2 text-sm text-red-700">
            {error}
          </p>
        ) : null}

        <p className="text-xs text-stone-500">
          An account will be created with a temporary password. Ask the student to
          sign in and complete their profile.
        </p>
      </div>
    </BottomSheet>
  );
};

export default QuickRegisterSheet;
